import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import LoginIcon from "@mui/icons-material/Login";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AddSubAdminModal from "./AddSubAdminModal";
import ManagedUserNotificationDialog from "./ManagedUserNotificationDialog";
import { useStatus } from "../../popup/NotificationContext";
import { loginSuccess } from "../../store/authSlice";
import {
  adminApi,
  useAssignUserToGroupAdminMutation,
  useDeleteUserMutation,
  useGetDevicesPageQuery,
  useEditDeviceMutation,
  useEditUserMutation,
  useGetAllLiteDevicesQuery,
  useGetAllUsersQuery,
  useImpersonateUserMutation,
  useRevealUserPasswordMutation,
  useResetPasswordMutation,
  useRestoreUserMutation,
} from "../../redux/admin/adminApi";
import { getUserActionsEffective, hasUserPermission, normalizeRole, ROLES } from "../../utils/rbacHelper";
import { getDeviceStatus as resolveVehicleStatus } from "../../utils/vehicleStatus";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", { hour12: true }).toUpperCase();
};

const getDeviceStatus = (device) => {
  return String(resolveVehicleStatus(device) || "no_data");
};

const matchesManagedUserId = (candidate, userId) => {
  if (!userId || !candidate) return false;
  return String(candidate?._id || candidate?.id || candidate) === String(userId);
};

const isDeviceOwnedByUser = (device, userId) => {
  if (!userId || !device) return false;
  return [
    device?.assignedToUser,
    device?.adminId,
    device?.createdBy,
    device?.subAdminOwner,
    device?.dealerOwner,
    device?.dealer,
  ].some((candidate) => matchesManagedUserId(candidate, userId));
};

const getStatusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("running")) return "#10b981";
  if (value.includes("stop")) return "#dc2626";
  if (value.includes("idle")) return "#f59e0b";
  return "#ef4444";
};

const ASSIGN_DEVICE_PAGE_SIZE = 25;

const buildCsv = (rows) => {
  const headers = [
    "Name",
    "User ID",
    "Mobile",
    "Created By",
    "Last Login",
    "Last Activity",
    "Device Limit",
    "Yearly Available Licenses",
    "Lifetime Available Licenses",
    "User Limit",
    "Manage Dealers Count",
    "Disabled",
  ];
  const data = rows.map((row) =>
    [
      row.username,
      row.uniqueId,
      row.mobileNo,
      row.createdByName,
      row.lastLoginLabel,
      row.lastActivityLabel,
      row.deviceLimit,
      row.yearlyAvailableLicenses,
      row.lifetimeAvailableLicenses,
      row.userLimit,
      row.manageDealersCount,
      row.disabled ? "Yes" : "No",
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...data].join("\n");
};

const resolveCreatedByLabel = (user) => {
  const getLabel = (candidate) => {
    if (!candidate) return "";
    const username = String(candidate?.username || "").trim();
    const uniqueId = String(candidate?.uniqueId || "").trim();
    if (username && uniqueId) return `${username} (${uniqueId})`;
    return username || uniqueId || "";
  };

  return (
    String(user?.createdByName || "").trim() ||
    getLabel(user?.createdBy) ||
    getLabel(user?.dealerOwner) ||
    getLabel(user?.subAdminOwner) ||
    "-"
  );
};

const formatActivityTimestamp = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatPresenceStatus = (value) => (value ? "Online" : "Offline");

export default function SubAdminPortalModal({ open = true, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserRole = normalizeRole(currentUser?.role);
  const userActions = getUserActionsEffective(currentUser);
  const canAssignDevice = hasUserPermission(currentUser, "assignDevice");
  const canAssignUserToGA = hasUserPermission(currentUser, "assignUserToGA");
  const { showStatus } = useStatus();

  const { data: usersPayload = [], refetch } = useGetAllUsersQuery();
  const [impersonateUser] = useImpersonateUserMutation();
  const [editUser] = useEditUserMutation();
  const [editDevice] = useEditDeviceMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [restoreUser] = useRestoreUserMutation();
  const [assignUserToGroupAdmin] = useAssignUserToGroupAdminMutation();
  const [revealUserPassword] = useRevealUserPasswordMutation();
  const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();

  const [activeTab, setActiveTab] = useState("list");
  const [searchName, setSearchName] = useState("");
  const [searchLoginId, setSearchLoginId] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [modalMode, setModalMode] = useState("table");
  const [editingSubAdmin, setEditingSubAdmin] = useState(null);
  const [resetDialogUser, setResetDialogUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(() => new Set());
  const [assignDeviceDialogUser, setAssignDeviceDialogUser] = useState(null);
  const [notificationDialogUser, setNotificationDialogUser] = useState(null);
  const [assignDeviceSearch, setAssignDeviceSearch] = useState("");
  const [assignDevicePage, setAssignDevicePage] = useState(1);
  const [pendingAssignIds, setPendingAssignIds] = useState(() => new Set());
  const [assignOverrides, setAssignOverrides] = useState(() => new Map());
  const [assignDeviceCache, setAssignDeviceCache] = useState(() => new Map());
  const [assignGroupDialogUser, setAssignGroupDialogUser] = useState(null);
  const [assignGroupTargetId, setAssignGroupTargetId] = useState("");

  const { data: devicesPayload = [] } = useGetAllLiteDevicesQuery(undefined, {
    skip: !open,
  });

  const normalizedUsers = useMemo(
    () => (Array.isArray(usersPayload) ? usersPayload : []),
    [usersPayload]
  );

  const normalizedSubAdmins = useMemo(
    () =>
      normalizedUsers
        .filter((user) => normalizeRole(user?.role) === ROLES.SUB_ADMIN)
        .map((user) => {
          const permissions = user?.permissions || {};
          const limits = permissions?.limits || {};
          const licenseSummary = user?.licenseSummary || {};
          const credits = user?.credits || {};
          const userId = String(user?._id || "");
          const managedSet = new Set(
            (Array.isArray(user?.managedUsers) ? user.managedUsers : [])
              .map((item) => String(item?._id || item || ""))
              .filter(Boolean)
          );
          const manageDealersCount = normalizedUsers.filter((candidate) => {
            const candidateRole = normalizeRole(candidate?.role);
            if (candidateRole !== ROLES.DEALER) return false;
            const candidateId = String(candidate?._id || "");
            const ownerId = String(candidate?.subAdminOwner || "");
            return ownerId === userId || managedSet.has(candidateId);
          }).length;

          return {
            ...user,
            role: normalizeRole(user?.role),
            disabled: user?.isActive === false,
            isOnline: Boolean(user?.isOnline),
            uniqueId: user?.uniqueId || "-",
            mobileNo: user?.mobileNo || "-",
            createdByName: resolveCreatedByLabel(user),
            lastLoginLabel: formatActivityTimestamp(user?.lastLoginAt),
            lastActivityLabel: formatActivityTimestamp(
              user?.lastActivityAt || user?.updatedAt || user?.createdAt
            ),
            lastSeenLabel: formatActivityTimestamp(user?.lastSeen),
            deviceLimit: numberValue(limits?.deviceLimit),
            userLimit: numberValue(limits?.userLimit),
            yearlyAvailableLicenses: numberValue(
              licenseSummary?.yearlyAvailableLicenses ??
                licenseSummary?.totalRemainingCredits ??
                user?.remainingTokens
            ),
            lifetimeAvailableLicenses: numberValue(
              licenseSummary?.lifetimeAvailableLicenses ?? credits?.balance
            ),
            manageDealersCount,
          };
        }),
    [normalizedUsers]
  );

  const safeDevices = useMemo(
    () =>
      (Array.isArray(devicesPayload) ? devicesPayload : [])
        .filter((device) => String(device?.status || "").toLowerCase() !== "deleted")
        .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || ""))),
    [devicesPayload]
  );

  const filteredSubAdmins = useMemo(() => {
    const nameQuery = searchName.trim().toLowerCase();
    const loginQuery = searchLoginId.trim().toLowerCase();
    const phoneQuery = searchPhone.trim();

    return normalizedSubAdmins.filter((subAdmin) => {
      if (nameQuery && !String(subAdmin?.username || "").toLowerCase().includes(nameQuery)) return false;
      if (loginQuery && !String(subAdmin?.uniqueId || "").toLowerCase().includes(loginQuery)) return false;
      if (phoneQuery && !String(subAdmin?.mobileNo || "").includes(phoneQuery)) return false;
      return true;
    });
  }, [normalizedSubAdmins, searchName, searchLoginId, searchPhone]);

  const assignDeviceQueryArgs = useMemo(() => {
    if (!assignDeviceDialogUser?._id) return null;
    const query = String(assignDeviceSearch || "").trim();
    return {
      page: assignDevicePage,
      limit: ASSIGN_DEVICE_PAGE_SIZE,
      lite: "true",
      includeDeleted: "false",
      includeExpired: "true",
      search: query || undefined,
    };
  }, [assignDeviceDialogUser?._id, assignDevicePage, assignDeviceSearch]);

  const {
    data: assignDevicePageResponse,
    isFetching: assignDevicesFetching,
    refetch: refetchAssignDevicePage,
  } = useGetDevicesPageQuery(assignDeviceQueryArgs, {
    skip: !open || !assignDeviceDialogUser?._id,
    refetchOnMountOrArgChange: true,
  });

  const fallbackAssignDeviceRows = useMemo(() => {
    const query = String(assignDeviceSearch || "").trim().toLowerCase();
    const filteredRows = !query
      ? safeDevices
      : safeDevices.filter((device) =>
          [
            device?.name,
            device?.vehicleNumber,
            device?.imei,
            device?.status,
            device?.liveData?.vehicleStatus,
            device?.msdn,
            device?.model,
            device?.vehicleType,
            device?.deviceIcon,
          ].some((value) => String(value || "").toLowerCase().includes(query))
        );
    const start = (assignDevicePage - 1) * ASSIGN_DEVICE_PAGE_SIZE;
    return filteredRows.slice(start, start + ASSIGN_DEVICE_PAGE_SIZE);
  }, [assignDevicePage, assignDeviceSearch, safeDevices]);

  const fallbackAssignDeviceTotal = useMemo(() => {
    const query = String(assignDeviceSearch || "").trim().toLowerCase();
    if (!query) return safeDevices.length;
    return safeDevices.filter((device) =>
      [
        device?.name,
        device?.vehicleNumber,
        device?.imei,
        device?.status,
        device?.liveData?.vehicleStatus,
        device?.msdn,
        device?.model,
        device?.vehicleType,
        device?.deviceIcon,
      ].some((value) => String(value || "").toLowerCase().includes(query))
    ).length;
  }, [assignDeviceSearch, safeDevices]);

  const assignDeviceApiRows = useMemo(
    () =>
      (Array.isArray(assignDevicePageResponse?.data)
        ? assignDevicePageResponse.data
        : Array.isArray(assignDevicePageResponse?.data?.data)
          ? assignDevicePageResponse.data.data
          : [])
        .filter((device) => String(device?.status || "").toLowerCase() !== "deleted")
        .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || ""))),
    [assignDevicePageResponse]
  );

  const shouldUseAssignDeviceFallback =
    safeDevices.length > 0 &&
    !Array.isArray(assignDevicePageResponse?.data) &&
    !Array.isArray(assignDevicePageResponse?.data?.data);

  const assignDevicePageRows = shouldUseAssignDeviceFallback ? fallbackAssignDeviceRows : assignDeviceApiRows;

  const assignDeviceTotal = shouldUseAssignDeviceFallback
    ? fallbackAssignDeviceTotal
    : Number(assignDevicePageResponse?.total ?? assignDevicePageResponse?.data?.total ?? 0);
  const assignDeviceTotalPages = Math.max(1, Math.ceil(assignDeviceTotal / ASSIGN_DEVICE_PAGE_SIZE));

  useEffect(() => {
    const userId = String(assignDeviceDialogUser?._id || "");
    if (!userId) {
      setAssignDeviceCache(new Map());
      return;
    }

    setAssignDeviceCache((prev) => {
      const next = new Map(prev);
      assignDevicePageRows.forEach((device) => {
        const deviceId = String(device?._id || device?.id || "");
        if (!deviceId) return;
        next.set(deviceId, {
          ...(next.get(deviceId) || {}),
          ...device,
        });
      });
      return next;
    });
  }, [assignDeviceDialogUser?._id, assignDevicePageRows]);

  useEffect(() => {
    if (assignDevicePage > assignDeviceTotalPages) {
      setAssignDevicePage(1);
    }
  }, [assignDevicePage, assignDeviceTotalPages]);

  const assignableDevices = useMemo(() => {
    const userId = String(assignDeviceDialogUser?._id || "");
    const rows = assignDevicePageRows.map((rawDevice) => {
      const device = assignDeviceCache.get(String(rawDevice?._id || rawDevice?.id || "")) || rawDevice;
      const deviceId = String(device?._id || device?.id || "");
      const baseChecked = isDeviceOwnedByUser(device, userId);
      const overrideChecked = assignOverrides.get(deviceId);
      const checked = typeof overrideChecked === "boolean" ? overrideChecked : baseChecked;
      return {
        id: deviceId,
        name: device?.name || device?.vehicleNumber || device?.imei || "-",
        imei: device?.imei || "-",
        status: getDeviceStatus(device),
        lastUpdate: formatTimestamp(device?.lastDataReceivedAt || device?.liveData?.timestamp),
        simNo: device?.msdn || "-",
        model: device?.model || "-",
        icon: device?.vehicleType || device?.deviceIcon || "Default",
        assignedToUserId: String(device?.assignedToUser?._id || device?.assignedToUser || ""),
        adminOwnerId: String(device?.adminId?._id || device?.adminId || ""),
        dealerOwnerId: String(device?.dealer?._id || device?.dealer || ""),
        checked,
      };
    });

    return rows.sort((left, right) => {
      if (left.checked !== right.checked) return Number(right.checked) - Number(left.checked);
      return String(left.name || "").localeCompare(String(right.name || ""));
    });
  }, [assignDeviceCache, assignDeviceDialogUser?._id, assignDevicePageRows, assignOverrides]);

  const groupAdmins = useMemo(
    () =>
      normalizedUsers.filter((user) => {
        const role = normalizeRole(user?.role);
        if (currentUserRole === ROLES.SUPER_ADMIN || currentUserRole === ROLES.MANAGER) {
          return role === ROLES.SUB_ADMIN || role === ROLES.DEALER;
        }
        if (currentUserRole === ROLES.OFFICE_EMPLOYEE) {
          return role === ROLES.SUB_ADMIN;
        }
        if (currentUserRole === ROLES.SUB_ADMIN) {
          return role === ROLES.DEALER;
        }
        return false;
      }),
    [currentUserRole, normalizedUsers]
  );

  const totalPages = Math.max(1, Math.ceil(filteredSubAdmins.length / rowsPerPage));
  const pagedSubAdmins = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredSubAdmins.slice(start, start + rowsPerPage);
  }, [filteredSubAdmins, page, rowsPerPage]);

  const stats = useMemo(
    () => ({
      totalSubAdmins: filteredSubAdmins.length,
      totalDeviceLimit: filteredSubAdmins.reduce((sum, item) => sum + item.deviceLimit, 0),
      totalUserLimit: filteredSubAdmins.reduce((sum, item) => sum + item.userLimit, 0),
      totalYearlyLicenses: filteredSubAdmins.reduce((sum, item) => sum + item.yearlyAvailableLicenses, 0),
      totalLifetimeLicenses: filteredSubAdmins.reduce((sum, item) => sum + item.lifetimeAvailableLicenses, 0),
      totalManagedDealers: filteredSubAdmins.reduce((sum, item) => sum + item.manageDealersCount, 0),
    }),
    [filteredSubAdmins]
  );
  const canRevealPasswords =
    currentUserRole === ROLES.SUPER_ADMIN || currentUserRole === ROLES.SUB_ADMIN;

  const clearRevealedPasswords = () => {
    setRevealedPasswords({});
    setLoadingPasswordIds(new Set());
  };

  const handleCloseModal = () => {
    clearRevealedPasswords();
    onClose?.();
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredSubAdmins);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "sub-admins.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Sub-admin CSV downloaded.");
  };

  const handleToggleDisabled = async (subAdmin, checked) => {
    try {
      await editUser({
        userId: subAdmin._id,
        isActive: !checked,
      }).unwrap();
      showStatus("success", "Sub-admin status updated.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update sub-admin status.");
    }
  };

  const handleLoginAsUser = async (targetUserId) => {
    if (!userActions.canLoginAsUser) return;
    try {
      const currentAdminToken = localStorage.getItem("token");
      if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);
      const response = await impersonateUser(targetUserId).unwrap();
      dispatch(adminApi.util.resetApiState());
      dispatch(loginSuccess({ user: response.user, token: response.token }));
      onClose?.();
      navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to login as sub-admin.");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId).unwrap();
      showStatus("success", "Sub-admin deleted.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete sub-admin.");
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await restoreUser(userId).unwrap();
      showStatus("success", "Sub-admin restored.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to restore sub-admin.");
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialogUser?._id || !newPassword.trim()) {
      showStatus("warning", "New password required.");
      return;
    }
    try {
      await resetPassword({ userId: resetDialogUser._id, password: newPassword }).unwrap();
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[String(resetDialogUser._id)];
        return next;
      });
      showStatus("success", "Password reset successful.");
      setResetDialogUser(null);
      setNewPassword("");
      setShowResetPassword(false);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reset password.");
    }
  };

  const handleToggleAssignDevice = async (deviceRow, nextChecked) => {
    if (!assignDeviceDialogUser?._id || !deviceRow?.id) return;
    const deviceId = String(deviceRow.id);
    const userId = String(assignDeviceDialogUser._id);
    const fallbackOwnerId = String(currentUser?._id || currentUser?.id || "");
    const previousOverride = assignOverrides.get(deviceId);
    const previousCacheEntry = assignDeviceCache.get(deviceId);
    const optimisticDevice = {
      ...(previousCacheEntry || {}),
      _id: previousCacheEntry?._id || deviceId,
      assignedToUser: nextChecked ? assignDeviceDialogUser._id : null,
      adminId:
        !nextChecked && String(deviceRow.adminOwnerId || "") === userId && fallbackOwnerId
          ? fallbackOwnerId
          : (previousCacheEntry?.adminId ?? deviceRow.adminOwnerId ?? null),
      dealer:
        !nextChecked && String(deviceRow.dealerOwnerId || "") === userId
          ? null
          : (previousCacheEntry?.dealer ?? deviceRow.dealerOwnerId ?? null),
    };

    setPendingAssignIds((prev) => new Set(prev).add(deviceId));
    setAssignDeviceCache((prev) => {
      const next = new Map(prev);
      next.set(deviceId, optimisticDevice);
      return next;
    });
    setAssignOverrides((prev) => {
      const next = new Map(prev);
      next.set(deviceId, Boolean(nextChecked));
      return next;
    });

    try {
      const payload = {
        deviceId,
        assignedToUser: nextChecked ? assignDeviceDialogUser._id : null,
      };

      if (!nextChecked) {
        if (String(deviceRow.adminOwnerId || "") === userId && fallbackOwnerId) {
          payload.adminId = fallbackOwnerId;
        }
        if (String(deviceRow.dealerOwnerId || "") === userId) {
          payload.dealer = null;
        }
      }

      await editDevice(payload).unwrap();
      showStatus("success", nextChecked ? "Device assigned successfully." : "Device unassigned successfully.");
      Promise.allSettled([refetchAssignDevicePage()]);
    } catch (error) {
      setAssignDeviceCache((prev) => {
        const next = new Map(prev);
        if (previousCacheEntry) next.set(deviceId, previousCacheEntry);
        else next.delete(deviceId);
        return next;
      });
      setAssignOverrides((prev) => {
        const next = new Map(prev);
        if (typeof previousOverride === "boolean") next.set(deviceId, previousOverride);
        else next.delete(deviceId);
        return next;
      });
      showStatus("error", error?.data?.msg || "Failed to assign device.");
    } finally {
      setPendingAssignIds((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  const handleAssignGroup = async () => {
    if (!assignGroupDialogUser?._id || !assignGroupTargetId) {
      showStatus("warning", "Please select an admin/dealer.");
      return;
    }
    try {
      await assignUserToGroupAdmin({
        groupAdminId: assignGroupTargetId,
        userIdToManage: assignGroupDialogUser._id,
      }).unwrap();
      showStatus("success", "Sub-admin assignment updated successfully.");
      setAssignGroupDialogUser(null);
      setAssignGroupTargetId("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to link sub-admin.");
    }
  };

  const closeForm = () => {
    setEditingSubAdmin(null);
    setModalMode("table");
  };

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "");
    if (!targetId || !canRevealPasswords) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const renderPasswordCell = (user) => {
    const userId = String(user?._id || "");
    const loading = loadingPasswordIds.has(userId);
    const revealedPassword = revealedPasswords[userId];
    const isVisible = Object.prototype.hasOwnProperty.call(revealedPasswords, userId);

    if (!canRevealPasswords) {
      return <VisibilityOffIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontWeight: 700, minWidth: 74 }}>
          {isVisible ? revealedPassword : "••••••••"}
        </Typography>
        <IconButton
          type="button"
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleTogglePassword(userId);
          }}
          disabled={loading}
          sx={{ color: "#2563eb" }}
        >
          {loading ? (
            <CircularProgress size={16} />
          ) : isVisible ? (
            <VisibilityIcon sx={{ fontSize: 18 }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Stack>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Admin</Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {modalMode === "table" ? (
            <>
              <Box sx={{ px: 3, pt: 1.5 }}>
                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} centered>
                  <Tab label="List" value="list" />
                  <Tab label="Statistics" value="statistics" />
                </Tabs>
              </Box>

              <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr auto" }, gap: 1.5, alignItems: "center" }}>
                  <TextField placeholder="Search by name" value={searchName} onChange={(e) => setSearchName(e.target.value)} sx={inputSx} InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }} />
                  <TextField placeholder="Search by Login Id" value={searchLoginId} onChange={(e) => setSearchLoginId(e.target.value)} sx={inputSx} InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }} />
                  <TextField placeholder="Search by phone" value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)} sx={inputSx} InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }} />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton onClick={() => setModalMode("create")} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                      <AddIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 88 }}>
                  <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                    {[10, 25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                    <RefreshIcon />
                  </IconButton>
                  <IconButton onClick={handleDownloadCsv} sx={{ color: "#6b7280" }}>
                    <DownloadIcon />
                  </IconButton>
                </Stack>
              </Box>

              {activeTab === "list" ? (
                <>
                  <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "56vh", borderRadius: 0 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {["", "Name", "Password", "User ID", "Mobile", "Created By", "Status", "Last Seen", "Last Login", "Last Activity", "Device Limit", "Yearly Available Licenses", "Lifetime Available Licenses", "User Limit", "Manage Dealers Count", "Disabled", "Actions"].map((head) => (
                            <TableCell key={head} sx={{ fontWeight: 700, color: "#4b5563", bgcolor: "#fff", borderBottom: "1px solid #d1d5db", whiteSpace: "nowrap" }}>
                              {head}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedSubAdmins.length ? pagedSubAdmins.map((subAdmin) => (
                          <TableRow key={subAdmin._id} hover>
                            <TableCell>□</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.username || "-"}</TableCell>
                            <TableCell>{renderPasswordCell(subAdmin)}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.uniqueId}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.mobileNo}</TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{subAdmin.createdByName}</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: subAdmin.isOnline ? "#16a34a" : "#6b7280", whiteSpace: "nowrap" }}>
                              {formatPresenceStatus(subAdmin.isOnline)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{subAdmin.lastSeenLabel}</TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{subAdmin.lastLoginLabel}</TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{subAdmin.lastActivityLabel}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.deviceLimit}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.yearlyAvailableLicenses}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.lifetimeAvailableLicenses}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.userLimit}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{subAdmin.manageDealersCount}</TableCell>
                            <TableCell>
                              <Switch size="small" checked={!!subAdmin.disabled} onChange={(event) => handleToggleDisabled(subAdmin, event.target.checked)} />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.6}>
                                {userActions.canLoginAsUser && (
                                  <Tooltip title="Login as Admin">
                                    <IconButton size="small" onClick={() => handleLoginAsUser(subAdmin._id)} sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}>
                                      <LoginIcon sx={{ fontSize: 18, color: "#111827" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {!subAdmin.isDeleted && canAssignDevice && (
                                  <Tooltip title="Assign Device">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setAssignDeviceDialogUser(subAdmin);
                                        setAssignDeviceSearch("");
                                        setPendingAssignIds(new Set());
                                        setAssignOverrides(new Map());
                                      }}
                                      sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                    >
                                      <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {!subAdmin.isDeleted && canAssignUserToGA && groupAdmins.length > 0 && (
                                  <Tooltip title="Link User To Group">
                                    <IconButton size="small" onClick={() => setAssignGroupDialogUser(subAdmin)} sx={{ bgcolor: "#2563eb", borderRadius: 1 }}>
                                      <PersonAddAlt1OutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {!subAdmin.isDeleted && (
                                  <Tooltip title="Notifications">
                                    <IconButton
                                      size="small"
                                      onClick={() => setNotificationDialogUser(subAdmin)}
                                      sx={{ bgcolor: "#fbbf24", borderRadius: 1 }}
                                    >
                                      <NotificationsActiveOutlinedIcon sx={{ fontSize: 18, color: "#111827" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {userActions.canEdit && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => { setEditingSubAdmin(subAdmin); setModalMode("edit"); }} sx={{ bgcolor: "#2563eb", borderRadius: 1 }}>
                                      <EditIcon sx={{ fontSize: 18, color: "#fff" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {userActions.canResetPassword && (
                                  <Tooltip title="Reset Password">
                                    <IconButton size="small" onClick={() => setResetDialogUser(subAdmin)} sx={{ bgcolor: "#facc15", borderRadius: 1 }}>
                                      <KeyIcon sx={{ fontSize: 18, color: "#111827" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {subAdmin.isDeleted
                                  ? userActions.canRestore && (
                                      <Tooltip title="Restore">
                                        <IconButton size="small" onClick={() => handleRestoreUser(subAdmin._id)} sx={{ bgcolor: "#22c55e", borderRadius: 1 }}>
                                          <RestoreFromTrashOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                        </IconButton>
                                      </Tooltip>
                                    )
                                  : userActions.canDelete && (
                                      <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => handleDeleteUser(subAdmin._id)} sx={{ bgcolor: "#ef4444", borderRadius: 1 }}>
                                          <DeleteIcon sx={{ fontSize: 18, color: "#fff" }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={17} align="center" sx={{ py: 6, color: "#6b7280" }}>
                              No sub-admins found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ color: "#374151" }}>
                      Showing {filteredSubAdmins.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredSubAdmins.length)} of {filteredSubAdmins.length} entries
                    </Typography>
                    <Pagination page={page} count={Math.max(1, totalPages)} onChange={(_, value) => setPage(value)} shape="rounded" />
                  </Box>
                </>
              ) : (
                <Box sx={{ px: 3, py: 3 }}>
                  <Grid container spacing={2}>
                    {[
                      { label: "Total Admins", value: stats.totalSubAdmins },
                      { label: "Total Device Limit", value: stats.totalDeviceLimit },
                      { label: "Total User Limit", value: stats.totalUserLimit },
                      { label: "Yearly Available Licenses", value: stats.totalYearlyLicenses },
                      { label: "Lifetime Available Licenses", value: stats.totalLifetimeLicenses },
                      { label: "Managed Dealers", value: stats.totalManagedDealers },
                    ].map((card) => (
                      <Grid item xs={12} sm={6} md={4} key={card.label}>
                        <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2, backgroundColor: "#fff", minHeight: 110 }}>
                          <Typography sx={{ fontSize: 13, color: "#6b7280", fontWeight: 700 }}>{card.label}</Typography>
                          <Typography sx={{ fontSize: 30, fontWeight: 900, mt: 1 }}>{card.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ p: 2 }}>
              <AddSubAdminModal
                open
                mode={modalMode === "edit" ? "edit" : "create"}
                initialData={editingSubAdmin}
                onClose={closeForm}
                onSuccess={async () => {
                  await refetch();
                  closeForm();
                  showStatus("success", modalMode === "edit" ? "Sub-admin updated." : "Sub-admin created.");
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ManagedUserNotificationDialog
        open={Boolean(notificationDialogUser)}
        onClose={() => setNotificationDialogUser(null)}
        targetUser={notificationDialogUser}
        devices={safeDevices}
        showStatus={showStatus}
        title="Admin Notifications"
      />

      <Dialog open={Boolean(resetDialogUser)} onClose={() => { setResetDialogUser(null); setNewPassword(""); setShowResetPassword(false); }} fullWidth maxWidth="xs">
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="New Password"
            type={showResetPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton edge="end" onClick={() => setShowResetPassword((prev) => !prev)}>
                  {showResetPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetDialogUser(null); setNewPassword(""); setShowResetPassword(false); }}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={isResettingPassword}>
            {isResettingPassword ? "Saving..." : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assignDeviceDialogUser)}
        onClose={() => {
          setAssignDeviceDialogUser(null);
          setAssignDeviceSearch("");
          setAssignDevicePage(1);
          setPendingAssignIds(new Set());
          setAssignOverrides(new Map());
          setAssignDeviceCache(new Map());
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "72vh" } }}
      >
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 900 }}>Assign Device</Typography>
            <IconButton
              onClick={() => {
                setAssignDeviceDialogUser(null);
                setAssignDeviceSearch("");
                setAssignDevicePage(1);
                setPendingAssignIds(new Set());
                setAssignOverrides(new Map());
                setAssignDeviceCache(new Map());
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ color: "#6b7280", fontWeight: 800, fontSize: 15, mb: 1.5 }}>Selected Customer</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, border: "1px solid #eef2f7" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Name", "Password", "User ID", "Mobile", "Device Limit", "Available Licenses", "User Limit", "Disabled"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#f8fafc", whiteSpace: "nowrap" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.username || "-"}</TableCell>
                    <TableCell>{renderPasswordCell(assignDeviceDialogUser)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.uniqueId || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.mobileNo || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{numberValue(assignDeviceDialogUser?.permissions?.limits?.deviceLimit)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {numberValue(
                        assignDeviceDialogUser?.licenseSummary?.yearlyAvailableLicenses ??
                        assignDeviceDialogUser?.licenseSummary?.totalRemainingCredits ??
                        assignDeviceDialogUser?.remainingTokens
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{numberValue(assignDeviceDialogUser?.permissions?.limits?.userLimit)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.isActive === false ? "Disabled" : "Not Disabled"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ px: 3, pb: 1.5 }}>
            <TextField
              placeholder="Search"
              value={assignDeviceSearch}
              onChange={(event) => {
                setAssignDeviceSearch(event.target.value);
                setAssignDevicePage(1);
              }}
              sx={{ ...inputSx, width: "100%" }}
              InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
            />
          </Box>

          <Box sx={{ px: 3, pb: 2.5 }}>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "46vh", borderRadius: 0, border: "1px solid #eef2f7" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48, bgcolor: "#fff" }} />
                    {["Vehicle No", "IMEI", "Status", "Last Update", "Sim No", "Model", "Icon"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 800, color: "#111827", bgcolor: "#fff", whiteSpace: "nowrap" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignableDevices.length ? (
                    assignableDevices.map((device) => {
                      return (
                        <TableRow key={device.id} hover>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={device.checked}
                              onChange={(event) => handleToggleAssignDevice(device, event.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.name}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.imei}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: getStatusColor(device.status) }}>
                            {String(device.status || "").replace(/_/g, " ")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.lastUpdate}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.simNo}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.model}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.icon}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : assignDevicesFetching ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#6b7280" }}>
                        No devices found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, gap: 2, flexWrap: "wrap" }}>
              <Typography sx={{ color: "#6b7280", fontWeight: 700 }}>
                Showing page {assignDevicePage} of {assignDeviceTotalPages}
              </Typography>
              <Pagination
                count={assignDeviceTotalPages}
                page={assignDevicePage}
                onChange={(_, value) => setAssignDevicePage(value)}
                color="primary"
                shape="rounded"
                size="small"
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setAssignDeviceDialogUser(null);
                  setAssignDeviceSearch("");
                  setAssignDevicePage(1);
                  setPendingAssignIds(new Set());
                  setAssignOverrides(new Map());
                  setAssignDeviceCache(new Map());
                }}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 900,
                  borderRadius: 1.2,
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assignGroupDialogUser)} onClose={() => { setAssignGroupDialogUser(null); setAssignGroupTargetId(""); }} fullWidth maxWidth="sm">
        <DialogTitle>Add Admin to Group</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label={currentUserRole === ROLES.OFFICE_EMPLOYEE ? "Select Sub-Admin" : currentUserRole === ROLES.SUB_ADMIN ? "Select Dealer" : "Select Admin/Dealer"}
            value={assignGroupTargetId}
            onChange={(event) => setAssignGroupTargetId(event.target.value)}
            sx={{ mt: 1 }}
          >
            {groupAdmins.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user?.username || user?.name || "Admin"}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignGroupDialogUser(null); setAssignGroupTargetId(""); }}>Cancel</Button>
          <Button onClick={handleAssignGroup} variant="contained">Link to Group</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
